// packages/core/src/learning/core-modules/income-is-a-lever.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 08: "Income Is a Lever" (tier: growth)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Verified refs:
//   openImpactLab categoryId 'career-income' (valid ImpactCategoryId)
//   openImpactScenario 'side-income-500', 'invested-vs-spent-500'
//   openSimulator scenarioType 'side-hustle' (valid ScenarioKind)
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'income-is-a-lever--income-is-a-control-lever',
	title: 'Your income is a control lever',
	description:
		'You learn to see income as one of the fastest ways to change your options, not just a number on a paycheck.',
	pillar: 'income-career-lever',
	schoolsOfThought: ['income-expansion', 'career-capital', 'human-capital'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['income', 'career', 'earning-power', 'options'],
	prerequisites: [],
	relatedLessons: ['income-is-a-lever--skills-become-future-income'],
	concepts: ['income-leverage', 'earning-capacity', 'option-building'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Most people treat income like weather. It happens to them, they react to it, and then they build their life around whatever shows up. But income is not only a paycheck. It is a lever that changes how much room you have to breathe, save, move, invest, say no, and recover from mistakes.',
		},
		{
			type: 'paragraph',
			text: 'Cutting expenses matters. You already learned that cash flow gives you control. But there is a ceiling to how much you can cut before life gets miserable. Income is different. It has no clean ceiling. A better job, a stronger skill, a raise, freelance work, side income, overtime, or a second income stream can change the whole shape of your money life.',
		},
		{
			type: 'bullet_list',
			items: [
				'More income can build runway faster',
				'More income can reduce debt pressure',
				'More income can fund investing without extreme sacrifice',
				'More income can make life decisions less desperate',
			],
		},
		{
			type: 'callout',
			icon: '💡',
			text: 'The point is not “just make more money.” The point is that earning power is a skill stack you can build over time.',
		},
		{
			type: 'example',
			label: '$300/month changes the room',
			text: 'If you create an extra $300 per month and keep your lifestyle the same, that is $3,600 per year. That could mean a starter emergency fund, faster debt payoff, a move-out cushion, training money, or a first investing habit. The amount is not magic. The breathing room is.',
		},
		{
			type: 'paragraph',
			text: 'This is why income matters so much in Nisk. Money buys options. More income, when it does not immediately disappear into lifestyle creep, buys more options faster. It lets you stop choosing between every good thing. It gives you more room to build stability and future freedom at the same time.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is earning more and automatically becoming more expensive to maintain. Higher income only helps if some of it turns into options.',
		},
		{
			type: 'paragraph',
			text: 'You do not need to become a startup founder or internet millionaire for income to matter. A $2/hour raise on full-time work is roughly $4,000 per year before taxes. A $500 monthly side project is $6,000 per year before taxes. A skill that gets you into a better role can change your next five years.',
		},
		{
			type: 'key_takeaway',
			text: 'Income is not just what you earn. It is how much future choice you can create before lifestyle absorbs it.',
		},
	],
	audioScript:
		'Income is one of the biggest levers in your money life, but most people do not treat it that way. They treat income like something fixed. “This is what I make, so this is my life.” And yes, there are real limits. Not everyone can instantly jump jobs, get promoted, or start earning more tomorrow. But that does not mean income is completely outside your control. It means you need to treat it like a skill stack, not a wish. Here is the simple reframe: expenses are defense, income is offense. Cutting waste helps you stop the bleeding. Earning more helps you create more room. Both matter. But there is a ceiling to cutting. You can only cancel so many subscriptions and eat at home so many times before you are just squeezing your life. Income has more upside. A better skill, stronger resume, cleaner portfolio, sharper interview ability, side work, overtime, a raise, or a better role can change what is possible. The key is not to let every new dollar become a new bill. If you earn $300 more each month and spend all $300 automatically, your life got bigger but not freer. If even part of that money turns into runway, debt progress, investing, or training, your options changed. This is why Nisk treats income as a lever. Not because money makes you better. It does not. But money can make you less trapped. More income, handled well, can buy you time, mobility, calm, and future choices. That is the point.',
	flashcards: [
		{
			id: 'income-is-a-lever--income-is-a-control-lever--fc-main',
			role: 'main_idea',
			front: 'Why is income a lever?',
			back: 'Because it can expand your options faster than cutting alone.',
		},
		{
			id: 'income-is-a-lever--income-is-a-control-lever--fc-watch',
			role: 'watch_out',
			front: 'What weakens higher income?',
			back: 'Lifestyle creep can absorb the raise before it builds freedom.',
		},
		{
			id: 'income-is-a-lever--income-is-a-control-lever--fc-use',
			role: 'use_it',
			front: 'How should extra income be tested?',
			back: 'Ask what part becomes runway, debt progress, investing, or skill growth.',
		},
	],
	applyAction: {
		type: 'openImpactLab',
		label: 'Explore income tradeoffs',
		categoryId: 'career-income',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'income-is-a-lever--skills-become-future-income',
	title: 'Skills become future income',
	description:
		'You learn why skills, proof, reputation, and experience are assets that can raise your earning power.',
	pillar: 'income-career-lever',
	schoolsOfThought: ['career-capital', 'human-capital', 'income-expansion'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['skills', 'career-growth', 'earning-power', 'human-capital'],
	prerequisites: ['income-is-a-lever--income-is-a-control-lever'],
	relatedLessons: ['income-is-a-lever--side-income-buys-breathing-room'],
	concepts: ['skill-stacking', 'career-capital', 'proof-of-work'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Your income is not only based on how hard you work. It is shaped by what problems you can solve, how clearly you can prove it, and whether people trust you with more responsibility. That is why skills are not just self-improvement. Skills are future income sitting in unfinished form.',
		},
		{
			type: 'paragraph',
			text: 'A skill becomes valuable when it helps someone save time, make money, reduce risk, fix pain, or create something useful. Coding, sales, design, operations, writing, project management, negotiation, repair work, caregiving, teaching, analytics, leadership, and trade skills can all become income levers when they solve real problems.',
		},
		{
			type: 'bullet_list',
			items: [
				'Skill is what you can do',
				'Proof is how people know you can do it',
				'Reputation is why they trust you',
				'Experience is how you handle harder problems',
			],
		},
		{
			type: 'callout',
			icon: '🧠',
			text: 'The market does not reward effort evenly. It usually rewards useful proof attached to valuable problems.',
		},
		{
			type: 'example',
			label: 'Small skill, real raise',
			text: 'Imagine two workers making $22/hour. One learns a tool that saves the team five hours a week and documents the result. That proof can support a raise conversation, a better resume bullet, or a move into a higher-paying role. The skill matters, but the proof makes it visible.',
		},
		{
			type: 'paragraph',
			text: 'This is where many people underplay themselves. They learn things, help people, solve problems, and improve systems, but never turn that into proof. No notes. No portfolio. No before-and-after. No numbers. No story. Then they wonder why a manager, client, or new employer does not see the value.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is collecting random learning without turning it into useful proof.',
		},
		{
			type: 'paragraph',
			text: 'You do not need to master everything. You need a tighter loop: pick a valuable problem, build a useful skill, create proof, then use that proof to ask for better opportunities. That might mean a raise, promotion, client, certification, portfolio piece, stronger interview, or new role.',
		},
		{
			type: 'key_takeaway',
			text: 'A skill starts paying when it solves a real problem and you can prove the result.',
		},
	],
	audioScript:
		'Skills are future income, but only when they connect to real problems. That part matters. You can watch videos forever, collect certificates, and feel productive, but if the skill does not help someone solve something, it may not change your income much. A useful skill saves time, makes money, reduces risk, fixes pain, or creates something people value. Then proof makes it visible. This is where the game changes. You might be good at something, but if nobody can see it, measure it, or trust it, it is harder to turn into more income. Think about a worker who learns a software tool and uses it to save the team five hours a week. That is not just “I learned a tool.” That is proof. That can become a resume bullet, a raise conversation, a promotion case, or a freelance offer. The lesson is not to become obsessed with credentials. It is to build career capital. Skill, proof, reputation, and experience stack together. Skill says, “I can do this.” Proof says, “Here is evidence.” Reputation says, “People trust me.” Experience says, “I can handle harder problems.” That stack can raise your earning power over time. The trap is random learning with no direction. You do not need fifty skills. You need a few useful ones connected to better opportunities. Pick a problem worth solving. Build the skill. Show proof. Use that proof to move.',
	flashcards: [
		{
			id: 'income-is-a-lever--skills-become-future-income--fc-main',
			role: 'main_idea',
			front: 'When do skills raise income?',
			back: 'When they solve valuable problems and are backed by visible proof.',
		},
		{
			id: 'income-is-a-lever--skills-become-future-income--fc-watch',
			role: 'watch_out',
			front: 'What is random learning?',
			back: 'Learning without a target problem, proof, or path to better opportunity.',
		},
		{
			id: 'income-is-a-lever--skills-become-future-income--fc-use',
			role: 'use_it',
			front: 'What should you document?',
			back: 'Before-and-after results, saved time, solved problems, and measurable impact.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Identify your income skill',
		prompt: 'Name one skill that could help you solve a more valuable problem in the next 90 days.',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'income-is-a-lever--side-income-buys-breathing-room',
	title: 'Side income buys breathing room',
	description:
		'You learn how small side income can create room without needing a dramatic life overhaul.',
	pillar: 'income-career-lever',
	schoolsOfThought: ['income-expansion', 'first-100k', 'cashflow-assets'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['side-hustle', 'extra-income', 'cash-flow', 'runway'],
	prerequisites: ['income-is-a-lever--skills-become-future-income'],
	relatedLessons: ['income-is-a-lever--raises-need-a-plan'],
	concepts: ['side-income', 'breathing-room', 'surplus-creation'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Side income gets overhyped online. People make it sound like everyone needs a brand, a business, a storefront, a course, or a full second life after work. That is too much. The real value of side income is simpler: it can buy breathing room before your main income changes.',
		},
		{
			type: 'paragraph',
			text: 'Breathing room means your life has more slack. A small amount of extra money can help you stop overdrafting, build a starter cushion, pay down a balance, fund training, cover car repairs, or start investing without squeezing every normal expense.',
		},
		{
			type: 'bullet_list',
			items: [
				'$100/month can start a habit',
				'$250/month can reduce pressure',
				'$500/month can change your yearly math',
				'$1,000/month can become a serious option engine',
			],
		},
		{
			type: 'callout',
			icon: '🛠️',
			text: 'Side income does not need to become your identity. It can simply be a temporary lever.',
		},
		{
			type: 'example',
			label: '$500/month',
			text: 'An extra $500 per month is $6,000 per year before taxes. That could be a debt payoff accelerator, a move-out fund, a certification fund, or a first investing habit. It may not make you rich. It can make you less stuck.',
		},
		{
			type: 'paragraph',
			text: 'Good side income usually starts with what you can already do or learn quickly. Tutoring. Weekend shifts. Freelance coding. Delivery work. Editing. Babysitting. Cleaning. Handyman work. Selling a service. Helping a small business. The point is not glamour. The point is controlled surplus.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is choosing side income that destroys your health, main job, relationships, or ability to think.',
		},
		{
			type: 'paragraph',
			text: 'Side income has a cost. It uses time, energy, attention, and sometimes upfront money. So the question is not “Should everyone side hustle?” The question is “Would a temporary income lever create more options than it costs?” For some people, yes. For others, a better main-job move is smarter.',
		},
		{
			type: 'key_takeaway',
			text: 'Side income is useful when it creates options without quietly burning down the rest of your life.',
		},
	],
	audioScript:
		'Side income is useful, but the internet makes it weird. It gets sold like everyone needs to become an entrepreneur after work, build a personal brand, and turn every hobby into money. That is not the point here. The point is breathing room. An extra $100, $250, or $500 a month can change your money life before your main paycheck changes. It can help you build a small cushion, stop relying on credit cards, pay down debt faster, fund training, or start investing. It does not have to be forever. It does not have to be impressive. It just has to create clean surplus. But be honest about the cost. Side income uses time and energy. If it destroys your sleep, hurts your main job, damages your relationships, or makes you feel like a machine, the math is not the whole story. A side hustle that makes $300 but burns you out so badly that you underperform at your main job may not be a win. The better question is: what kind of extra income fits your season? Maybe it is weekend shifts for three months. Maybe it is freelance work. Maybe it is tutoring. Maybe it is selling a service. Maybe it is not side income at all, and the better lever is a raise or job switch. The lesson is to treat side income as a tool, not a personality. Use it when it buys breathing room. Stop when the cost gets too high.',
	flashcards: [
		{
			id: 'income-is-a-lever--side-income-buys-breathing-room--fc-main',
			role: 'main_idea',
			front: 'What can side income buy?',
			back: 'Breathing room, runway, debt progress, training money, or investing capacity.',
		},
		{
			id: 'income-is-a-lever--side-income-buys-breathing-room--fc-watch',
			role: 'watch_out',
			front: 'What can side income cost?',
			back: 'Time, energy, sleep, focus, relationships, and main-job performance.',
		},
		{
			id: 'income-is-a-lever--side-income-buys-breathing-room--fc-use',
			role: 'use_it',
			front: 'How should side income fit?',
			back: 'It should create options without burning down your health or main path.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Test $500/month side income',
		scenarioId: 'side-income-500',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'income-is-a-lever--raises-need-a-plan',
	title: 'Raises need a plan',
	description:
		'You learn why new income should be assigned before lifestyle creep quietly spends it for you.',
	pillar: 'income-career-lever',
	schoolsOfThought: [
		'income-expansion',
		'rich-life-ramit',
		'behavioral-finance',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['raises', 'lifestyle-creep', 'income-plan', 'spending-plan'],
	prerequisites: ['income-is-a-lever--side-income-buys-breathing-room'],
	relatedLessons: ['income-is-a-lever--career-moves-are-money-decisions'],
	concepts: [
		'raise-planning',
		'lifestyle-creep-defense',
		'intentional-upgrades',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'A raise feels like progress. And it is. But a raise without a plan can disappear so smoothly you barely notice it. A nicer apartment, more takeout, upgraded subscriptions, more weekends out, a bigger car payment, more convenience spending — suddenly the raise is gone and your stress did not move.',
		},
		{
			type: 'paragraph',
			text: 'This does not mean you should never enjoy higher income. That is too rigid. Money is supposed to improve your life. The problem is automatic lifestyle creep: when your life gets more expensive by default instead of by choice.',
		},
		{
			type: 'bullet_list',
			items: [
				'Some can upgrade your life now',
				'Some can build emergency runway',
				'Some can pay down expensive debt',
				'Some can go toward investing or future goals',
			],
		},
		{
			type: 'callout',
			icon: '🎯',
			text: 'A raise is strongest when you decide its job before your habits decide for you.',
		},
		{
			type: 'example',
			label: '$400/month raise',
			text: 'Suppose your take-home pay rises by $400 per month. One version spends all $400 without noticing. Another version puts $150 toward life upgrades, $150 toward debt or runway, and $100 toward long-term goals. Same raise. Different future.',
		},
		{
			type: 'paragraph',
			text: 'The best raise plan is not always “save everything.” That can backfire if your life feels like punishment. A sustainable plan usually gives your present self something and your future self something. You get to feel progress now while still buying options later.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is confusing “I make more now” with “I can afford every new recurring bill.”',
		},
		{
			type: 'paragraph',
			text: 'Recurring upgrades are the dangerous part. A one-time celebration is limited. A recurring bill follows you. Every monthly commitment lowers the amount of future income that is still free to use. Before you add a new monthly cost, ask whether it is worth giving up that flexibility.',
		},
		{
			type: 'key_takeaway',
			text: 'A raise should improve your life and your options — not just raise your monthly burn rate.',
		},
	],
	audioScript:
		'Raises are dangerous in a sneaky way. They feel like success, and they are success, but without a plan they can vanish. You get a few hundred more per month, then life expands. Better food, more convenience, more subscriptions, nicer weekends, maybe a bigger fixed payment. Three months later, you technically earn more, but you do not feel freer. That is lifestyle creep. The answer is not to become joyless and save every dollar. That is not the Nisk angle. Money should make your life better. The answer is to assign the raise before your habits do. Give the money jobs. Some can upgrade your life now. Some can build runway. Some can reduce debt pressure. Some can go toward investing or future goals. The split depends on the person, but the principle stays the same: decide intentionally. A $400 monthly raise can disappear into random spending, or it can be split between present life and future options. Same raise, different result. The biggest thing to watch is recurring costs. A one-time celebration is fine. A new monthly bill changes your baseline. It makes your life more expensive every month after this one. So before you add another fixed cost, ask: do I want this enough to give up some flexibility? A raise is powerful when it makes your life better without making you trapped at a higher income level.',
	flashcards: [
		{
			id: 'income-is-a-lever--raises-need-a-plan--fc-main',
			role: 'main_idea',
			front: 'Why plan a raise?',
			back: 'Because new income disappears fast when habits assign it automatically.',
		},
		{
			id: 'income-is-a-lever--raises-need-a-plan--fc-watch',
			role: 'watch_out',
			front: 'What is automatic lifestyle creep?',
			back: 'Letting your life get more expensive by default instead of by choice.',
		},
		{
			id: 'income-is-a-lever--raises-need-a-plan--fc-use',
			role: 'use_it',
			front: 'How can a raise be split?',
			back: 'Give some to present life and some to future options like runway or goals.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Compare spending vs investing $500',
		scenarioId: 'invested-vs-spent-500',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'income-is-a-lever--career-moves-are-money-decisions',
	title: 'Career moves are money decisions',
	description:
		'You learn to evaluate career choices by income, skill growth, stability, time, and future optionality.',
	pillar: 'income-career-lever',
	schoolsOfThought: ['career-capital', 'life-design', 'income-expansion'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['career-decisions', 'job-change', 'promotion', 'opportunity-cost'],
	prerequisites: ['income-is-a-lever--raises-need-a-plan'],
	relatedLessons: ['income-is-a-lever--income-is-a-control-lever'],
	concepts: ['career-optionality', 'opportunity-cost', 'income-paths'],
	blocks: [
		{
			type: 'paragraph',
			text: 'A career move is not just a job decision. It is a money decision, time decision, energy decision, and future-options decision all at once. The paycheck matters, but it is not the only variable. A higher-paying role can be a trap if it burns you out or teaches nothing. A lower-paying role can be smart if it builds a skill that opens better doors later.',
		},
		{
			type: 'paragraph',
			text: 'The mistake is judging career choices only by the next paycheck. Better income matters, especially when you need stability. But your career also has compounding. Skills, network, proof, reputation, and confidence can stack into better opportunities later.',
		},
		{
			type: 'bullet_list',
			items: [
				'Pay: what changes now',
				'Skill: what you become able to do',
				'Proof: what you can show later',
				'Stability: how risky the path is',
				'Energy: what it costs to sustain',
			],
		},
		{
			type: 'callout',
			icon: '🧭',
			text: 'A good career move should improve either your present stability, your future earning power, or both.',
		},
		{
			type: 'example',
			label: 'Two job offers',
			text: 'Offer A pays $5,000 more but gives no growth, long commutes, and high stress. Offer B pays the same as today but teaches a valuable skill, gives stronger mentorship, and creates better proof for the next role. Offer A may still be right if you need cash now. Offer B may be stronger if you can afford the short-term tradeoff. The point is to see the tradeoff clearly.',
		},
		{
			type: 'paragraph',
			text: 'This is not about chasing status. A fancy title that ruins your life is not freedom. A stable job that protects your health and gives you time to build may be underrated. A harder job that grows your skill fast may be worth it for a season. The answer depends on what the move changes in real life.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is staying comfortable so long that your earning power quietly stalls.',
		},
		{
			type: 'paragraph',
			text: 'Career capital needs maintenance. If your work is not growing your skills, proof, relationships, or income path, you may be trading years for a paycheck without building many options. Sometimes that is necessary. But you should know when it is happening.',
		},
		{
			type: 'key_takeaway',
			text: 'The best career move is not always the highest paycheck. It is the move that improves your options after the tradeoff.',
		},
	],
	audioScript:
		'Career moves are money decisions, but not in the shallow way people think. Yes, pay matters. If you need money now, pay matters a lot. But a job is also where you spend your time, energy, attention, and learning years. That means a career move can either build future options or quietly shrink them. A higher-paying job might help you build runway faster, pay down debt, or invest more. That is real. But if it burns you out, kills your health, teaches nothing, or traps you in a path you hate, the paycheck is not the whole story. On the other side, a job that pays the same or even slightly less might be worth considering if it builds a valuable skill, gives better mentorship, creates proof, or opens a stronger income path later. This is not advice to take less money. It is a reminder to evaluate the full tradeoff. Look at pay, skill, proof, stability, energy, commute, flexibility, and future opportunities. A good career move should improve your present stability, your future earning power, or both. The trap is staying comfortable while your earning power stalls. Sometimes you need a stable season. That is fine. But do not confuse stable with growing. Ask what your current role is building. If the answer is “nothing except another year gone,” that is information. Career capital compounds too. Treat it like an asset.',
	flashcards: [
		{
			id: 'income-is-a-lever--career-moves-are-money-decisions--fc-main',
			role: 'main_idea',
			front: 'Why are career moves financial?',
			back: 'They shape income, skills, stability, energy, and future options.',
		},
		{
			id: 'income-is-a-lever--career-moves-are-money-decisions--fc-watch',
			role: 'watch_out',
			front: 'What is the paycheck trap?',
			back: 'Judging a job only by pay while ignoring growth, risk, and energy cost.',
		},
		{
			id: 'income-is-a-lever--career-moves-are-money-decisions--fc-use',
			role: 'use_it',
			front: 'What should a career move improve?',
			back: 'Present stability, future earning power, or ideally both.',
		},
	],
	applyAction: {
		type: 'openSimulator',
		label: 'Model a higher-income path',
		scenarioType: 'side-hustle',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'income-is-a-lever',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'income-is-a-lever--cq--q1',
			question:
				'What is the main reason Module 8 treats income as a lever?',
			options: [
				'Because income automatically makes people wealthy',
				'Because income can expand options faster than cutting alone',
				'Because expenses do not matter',
				'Because everyone should start a business',
			],
			correctIndex: 1,
			explanation:
				'Income is a lever because it can create more room for runway, debt progress, investing, and choices. It does not replace expense control.',
		},
		{
			type: 'true_false',
			id: 'income-is-a-lever--cq--q2',
			question:
				'Higher income only helps if at least some of it turns into more options instead of being fully absorbed by lifestyle.',
			correct: true,
			explanation:
				'More income can disappear through lifestyle creep. It becomes powerful when part of it builds stability or future freedom.',
		},
		{
			type: 'scenario',
			id: 'income-is-a-lever--cq--q3',
			scenario:
				'You get an extra $300 per month from overtime. Your first instinct is to upgrade several subscriptions and eat out more often.',
			question: 'What is the strongest Nisk-style response?',
			options: [
				'Spend it all because you earned it',
				'Save all of it and never enjoy the raise',
				'Decide what portion improves life now and what portion builds options',
				'Ignore it because $300 is too small to matter',
			],
			correctIndex: 2,
			explanation:
				'The point is intentionality. A split can improve present life while still building future options.',
		},
		{
			type: 'multiple_choice',
			id: 'income-is-a-lever--cq--q4',
			question: 'What makes a skill more likely to raise earning power?',
			options: [
				'It sounds impressive online',
				'It solves a valuable problem and has visible proof',
				'It takes a long time to learn',
				'It is unrelated to your work',
			],
			correctIndex: 1,
			explanation:
				'Useful skills create income leverage when they solve real problems and can be proven to employers, clients, or customers.',
		},
		{
			type: 'true_false',
			id: 'income-is-a-lever--cq--q5',
			question:
				'Random learning is always the best way to grow income because any learning eventually pays.',
			correct: false,
			explanation:
				'Learning helps most when it connects to a valuable problem, useful proof, and a path to better opportunity.',
		},
		{
			type: 'scenario',
			id: 'income-is-a-lever--cq--q6',
			scenario:
				'Someone can earn $500 per month doing weekend work, but it leaves them exhausted and hurts their main job performance.',
			question: 'What should they consider?',
			options: [
				'Only the extra $500 matters',
				'Side income is always worth it',
				'The time, energy, health, and main-job costs matter too',
				'They should quit their main job immediately',
			],
			correctIndex: 2,
			explanation:
				'Side income is a tool. It is useful when the options it creates are worth the hidden costs.',
		},
		{
			type: 'multiple_choice',
			id: 'income-is-a-lever--cq--q7',
			question:
				'What is the cleanest definition of side income in this module?',
			options: [
				'A guaranteed path to wealth',
				'A temporary or ongoing lever that can create extra breathing room',
				'A requirement for every adult',
				'A replacement for learning career skills',
			],
			correctIndex: 1,
			explanation:
				'Side income is framed as a lever for breathing room, not a universal requirement or guaranteed wealth path.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'income-is-a-lever--final--q1',
			question: 'Which statement best captures Module 8?',
			options: [
				'Income is fixed, so only budgeting matters',
				'Income is a lever that can expand options when handled intentionally',
				'Side income is always better than career growth',
				'Raises should always be spent immediately',
			],
			correctIndex: 1,
			explanation:
				'The module frames income as a controllable lever through skills, career capital, side income, raise planning, and career choices.',
		},
		{
			type: 'true_false',
			id: 'income-is-a-lever--final--q2',
			question:
				'Expense control matters, but it has a practical ceiling; income often has more upside over time.',
			correct: true,
			explanation:
				'Cutting expenses can create control, but income growth can expand the total amount of room available.',
		},
		{
			type: 'scenario',
			id: 'income-is-a-lever--final--q3',
			scenario:
				'Maya gets a $2/hour raise. She works full-time and wants to use the raise well without feeling deprived.',
			question: 'Which plan best fits the lesson?',
			options: [
				'Let the raise disappear into daily spending',
				'Add a new car payment immediately',
				'Give the raise jobs across life upgrades and future options',
				'Pretend the raise does not matter',
			],
			correctIndex: 2,
			explanation:
				'A raise is strongest when assigned intentionally before lifestyle creep absorbs it.',
		},
		{
			type: 'multiple_choice',
			id: 'income-is-a-lever--final--q4',
			question: 'What is career capital?',
			options: [
				'Only your job title',
				'The stack of skills, proof, reputation, and experience that improves future opportunities',
				'Money sitting in a checking account',
				'A type of investment account',
			],
			correctIndex: 1,
			explanation:
				'Career capital is the non-cash asset that can increase future earning power.',
		},
		{
			type: 'scenario',
			id: 'income-is-a-lever--final--q5',
			scenario:
				'Jordan keeps taking courses but never builds projects, documents results, or applies the skills to real problems.',
			question: 'What is the main issue?',
			options: [
				'Jordan is learning too slowly',
				'Jordan is building random learning without proof',
				'Jordan should stop learning forever',
				'Jordan needs only a better budget',
			],
			correctIndex: 1,
			explanation:
				'Skills become income leverage when attached to valuable problems and visible proof.',
		},
		{
			type: 'true_false',
			id: 'income-is-a-lever--final--q6',
			question:
				'Side income should be treated as a tool, not an identity or universal requirement.',
			correct: true,
			explanation:
				'Side income can be useful, but it has costs and should fit the person’s season, energy, and goals.',
		},
		{
			type: 'scenario',
			id: 'income-is-a-lever--final--q7',
			scenario:
				'Ana earns an extra $500 per month from freelance work. She spends all of it automatically and still feels just as trapped.',
			question: 'What did the module warn about?',
			options: [
				'Taxes are the only issue',
				'Extra income is useless',
				'Lifestyle can absorb new income before it creates options',
				'Freelance work never works',
			],
			correctIndex: 2,
			explanation:
				'New income only changes freedom if some of it becomes runway, debt progress, investing, training, or another option-building use.',
		},
		{
			type: 'multiple_choice',
			id: 'income-is-a-lever--final--q8',
			question:
				'What is the biggest danger of recurring upgrades after a raise?',
			options: [
				'They are always illegal',
				'They permanently raise your monthly burn rate',
				'They increase your credit score automatically',
				'They remove the need for budgeting',
			],
			correctIndex: 1,
			explanation:
				'Recurring costs follow you every month and reduce future flexibility.',
		},
		{
			type: 'scenario',
			id: 'income-is-a-lever--final--q9',
			scenario:
				'Two jobs are available. One pays more now but offers no growth and high stress. The other pays the same but builds a valuable skill and stronger proof.',
			question: 'What is the best way to think about the decision?',
			options: [
				'Always choose the higher pay',
				'Always choose the lower stress job',
				'Compare pay, skill growth, proof, stability, energy, and future options',
				'Ignore money entirely',
			],
			correctIndex: 2,
			explanation:
				'Career moves are financial decisions with multiple variables, not just paycheck comparisons.',
		},
		{
			type: 'true_false',
			id: 'income-is-a-lever--final--q10',
			question:
				'A higher-paying role can still be a poor fit if the hidden costs are too high.',
			correct: true,
			explanation:
				'Pay matters, but energy, health, stability, commute, growth, and future options also matter.',
		},
		{
			type: 'scenario',
			id: 'income-is-a-lever--final--q11',
			scenario:
				'Sam’s job is stable, but after three years it has not grown his skills, proof, network, or income path.',
			question: 'What is the most useful insight?',
			options: [
				'Stability is always bad',
				'Sam should quit immediately',
				'Sam may be trading years for a paycheck without building many future options',
				'Income does not matter',
			],
			correctIndex: 2,
			explanation:
				'The module does not shame stable work, but it asks whether the role is building future optionality.',
		},
		{
			type: 'multiple_choice',
			id: 'income-is-a-lever--final--q12',
			question:
				'What is the through-line connecting skills, side income, raises, and career moves?',
			options: [
				'They are unrelated income tricks',
				'Each is a way to grow or protect earning power so more of it becomes future options',
				'They all require quitting your job',
				'They only matter for high earners',
			],
			correctIndex: 1,
			explanation:
				'The module ties every lever back to the same goal: turning earning power into more options instead of more burn rate.',
		},
	],
};

export const INCOME_IS_A_LEVER_MODULE: LearningModule = {
	id: 'income-is-a-lever',
	title: 'Income Is a Lever',
	description:
		'Income is not fixed weather — it is a lever you can build. Learn how skills, side income, raises, and career moves grow your earning power, and why higher income only helps when some of it becomes options instead of burn rate.',
	emoji: '💪',
	accentColor: '#c8ff00',
	tier: 'growth',
	pillar: 'income-career-lever',
	order: 8,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
